import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PrintButton from '../../utils/print';
import PrintLetterhead from '../common/PrintLetterhead';
import FxAmount from '../common/FxAmount';
import useExchangeRates from '../common/useExchangeRates';
import { useCompany } from '../common/useCompany';

export default function CustomerList() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const company = useCompany();
  const base = company?.base_currency || 'USD';
  const { getRate } = useExchangeRates();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const listRef = useRef(null);

  const load = (q = search) => {
    customersAPI.list({ search: q }).then(setCustomers).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [search]);

  const doDelete = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`Delete customer "${c.name}"?`)) return;
    try {
      await customersAPI.remove(c.id);
      toast.success('Customer deleted');
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <div className="toolbar">
        <h2>Customers</h2>
        <div className="toolbar-right">
          <input className="form-control" placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} style={{width:250}} />
          <PrintButton targetRef={listRef} title={`Customers - ${new Date().toLocaleDateString()}`} />
          {hasPermission('customers:create') && <button className="btn btn-primary" onClick={() => navigate('/customers/new')}>+ New Customer</button>}
        </div>
      </div>

      {loading ? <div className="spinner" style={{margin:'40px auto'}} /> : (
        <div className="card" ref={listRef}>
          <div className="card-body" style={{padding:0}}>
            <PrintLetterhead />
            <table>
              <thead>
                <tr><th>#</th><th>Name</th><th>Email</th><th>Phone</th><th className="text-right">Balance ({base})</th><th>Status</th><th style={{width:80}}></th></tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)} style={{cursor:'pointer'}}>
                    <td className="font-mono">{c.customer_number}</td>
                    <td><strong>{c.name}</strong></td>
                    <td className="text-muted">{c.email || '-'}</td>
                    <td>{c.phone || '-'}</td>
                    <td className={`amount ${parseFloat(c.balance) > 0 ? 'negative' : ''}`}>
                      {/* The balance is kept in the company base currency; the
                          sub-line restates it in the customer's own currency so
                          a USD account is never read as AFN. */}
                      <FxAmount
                        amount={c.balance}
                        currency={base}
                        baseCurrency={base}
                        convertTo={c.currency}
                        rate={getRate(c.currency, base)}
                        style={{ textAlign: 'right' }}
                      />
                    </td>
                    <td>{c.is_active ? <span className="badge badge-posted">Active</span> : <span className="badge badge-voided">Inactive</span>}</td>
                    <td style={{whiteSpace:'nowrap'}}>
                      <button className="btn btn-ghost btn-sm" title="Edit" onClick={(e)=>{ e.stopPropagation(); navigate(`/customers/${c.id}`); }}><Pencil size={13} /></button>
                      <button className="btn btn-ghost btn-sm" title="Delete" onClick={(e)=>doDelete(e, c)}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
                {customers.length === 0 && <tr><td colSpan={7} className="empty-state"><div className="empty-icon">👤</div><h3>No customers yet</h3><p>Add your first customer to get started.</p></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
